import { useState, useEffect } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Avatar, Box, IconButton, Typography, useTheme } from "@mui/material";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../theme";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import { useResponsive } from "../hooks/uiHook";
import SideBarItem from "../components/sideBarItem";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuthUser, selectAuthUser } from "../features/authSlice";
import { getUserRoleLabel } from "../models/user.model";
import { useTranslation } from "react-i18next";
import { isRtlLanguage } from "../i18n";
import PermMediaIcon from '@mui/icons-material/PermMedia';
import GroupIcon from '@mui/icons-material/Group';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import NoFoodIcon from '@mui/icons-material/NoFood';
const getInitials = (fullName) => {
    if (!fullName) return "";
    const words = fullName.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "";
    const first = words[0].charAt(0);
    const last = words[words.length - 1].charAt(0);
    return `${first}${last}`.toUpperCase();
};

const OutterSideBar = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { isSmallScreen } = useResponsive();
    const [isCollapsed, setIsCollapsed] = useState(isSmallScreen);

    useEffect(() => {
        setIsCollapsed(isSmallScreen);
    }, [isSmallScreen]);
    const user = useSelector(selectAuthUser);

    useEffect(() => {
        if (!user) dispatch(fetchAuthUser());
    }, [dispatch, user]);

    return (
        <Box
            sx={{
                "& .pro-sidebar-inner": {
                    background: `${colors.primary[400]} !important`,
                    borderInlineEnd: `1px solid ${theme.palette.divider}`,
                },
                "& .pro-icon-wrapper": {
                    backgroundColor: "transparent !important",
                },
                "& .pro-inner-item": {
                    margin: "0 12px !important",
                    padding: "8px 12px !important",
                    borderRadius: "8px",
                    transition: "background-color 0.2s ease, color 0.2s ease",
                    overflow: "hidden",
                },
                "& .pro-inner-item .pro-item-content": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                },
                "& .pro-inner-item:hover": {
                    color: `${colors.redAccent[500]} !important`,
                    backgroundColor: `${colors.redAccent[500]}1a !important`,
                },
                "& .pro-inner-item:hover .sidebar-item-title": {
                    color: `${colors.redAccent[500]} !important`,
                },
                "& .pro-inner-item:hover .pro-icon-wrapper svg": {
                    color: `${colors.redAccent[500]} !important`,
                },
                "& .pro-menu-item.active": {
                    color: `${colors.redAccent[500]} !important`,
                },
            }}
            className="sidebar"
        >
            <Box sx={{ height: "100vh", display: "flex" }}>
                <Box>
                    <ProSidebar collapsed={isCollapsed} rtl={isRtlLanguage(i18n.language)}>
                        <Menu iconShape="square">
                            <MenuItem
                                onClick={() =>
                                    setIsCollapsed(isSmallScreen ? true : !isCollapsed)
                                }
                                icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
                                style={{
                                    margin: "10px 0 20px 0",
                                    color: colors.grey[100],
                                }}
                            >
                                {!isCollapsed && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        ml="15px"
                                    >
                                        <Typography variant="h3" color={colors.grey[100]}>
                                            {t("nav.dashboard")}
                                        </Typography>
                                        <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                                            <MenuOutlinedIcon />
                                        </IconButton>
                                    </Box>
                                )}
                            </MenuItem>

                            {!isCollapsed && user && (
                                <Box mb="25px">
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <Avatar
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                bgcolor: `${colors.redAccent[500]}1a`,
                                                color: colors.redAccent[500],
                                                fontWeight: "bold",
                                                fontSize: "1.5rem",
                                            }}
                                        >
                                            {getInitials(user.fullName || t("common.adminName"))}
                                        </Avatar>
                                    </Box>
                                    <Box textAlign="center">
                                        <Typography
                                            variant="h4"
                                            color={colors.grey[100]}
                                            fontWeight="bold"
                                            sx={{ m: "10px 0 0 0" }}
                                        >
                                            {user.fullName ? user.fullName : t("common.adminName")}
                                        </Typography>
                                        <Typography variant="h5" color={colors.redAccent[500]}>
                                            {getUserRoleLabel(user.role)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            <Box paddingLeft={isCollapsed ? undefined : "4px"}>
                                <SideBarItem
                                    title={t("nav.myRestaurants")}
                                    to="/restaurants"
                                    icon={<RestaurantIcon />}
                                />
                                <SideBarItem
                                    title={t("nav.allergy")}
                                    to="/allergy"
                                    icon={<NoFoodIcon />}
                                />
                                <SideBarItem
                                    title={t("nav.currency")}
                                    to="/currency-management"
                                    icon={<EuroSymbolIcon />}
                                />
                                <SideBarItem
                                    title={t("nav.userPermissions")}
                                    to="/user-permissions"
                                    icon={<GroupIcon />}
                                />
                                <SideBarItem
                                    title={t("nav.media")}
                                    to="/media"
                                    icon={<PermMediaIcon />}
                                />
                            </Box>
                        </Menu>
                    </ProSidebar>
                </Box>
            </Box>
        </Box>
    );
};

export default OutterSideBar;
